import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    ReactNode,
    useMemo,
    useRef,
} from 'react';
import {
    KidsCourse,
    KidsCourseTag,
    KidsCourseAgeGroup,
    Lang,
    KidsCoursesSettings,
    GetKidsCourses,
} from 'types';
import { getKidsCourseApiService } from 'services';

interface KidsCoursesContextType {
    projects: KidsCourse[];
    materials: KidsCourse[];
    settings: KidsCoursesSettings | null;

    isProjectsLoading: boolean;
    isMaterialsLoading: boolean;
    isSettingsLoading: boolean;

    hasMoreProjects: boolean;
    hasMoreMaterials: boolean;

    projectsTotal: number;
    materialsTotal: number;

    fetchSettings: (domain: Lang) => Promise<void>;

    fetchItems: (
        tag: KidsCourseTag,
        isLoadMore?: boolean,
        domain?: Lang,
        ageGroups?: KidsCourseAgeGroup[]
    ) => Promise<void>;
}

const KidsCoursesContext = createContext<KidsCoursesContextType | undefined>(
    undefined
);

export const KidsCoursesProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const apiService = useMemo(() => getKidsCourseApiService(), []);

    const [projects, setProjects] = useState<KidsCourse[]>([]);
    const projectsPageRef = useRef<number>(0);
    const [projectsTotal, setProjectsTotal] = useState<number>(0);
    const [isProjectsLoading, setIsProjectsLoading] = useState<boolean>(false);

    const [materials, setMaterials] = useState<KidsCourse[]>([]);
    const materialsPageRef = useRef<number>(0);
    const [materialsTotal, setMaterialsTotal] = useState<number>(0);
    const [isMaterialsLoading, setIsMaterialsLoading] =
        useState<boolean>(false);

    const [settings, setSettings] = useState<KidsCoursesSettings | null>(null);
    const [isSettingsLoading, setIsSettingsLoading] = useState<boolean>(false);

    const PAGE_SIZE = 4;

    const fetchSettings = useCallback(
        async (domain: Lang) => {
            setIsSettingsLoading(true);
            try {
                const { data } = await apiService.getSettings({
                    domain,
                });
                setSettings(data);
            } catch (error) {
                console.error(error);
            } finally {
                setIsSettingsLoading(false);
            }
        },
        [apiService]
    );

    const fetchItems = useCallback(
        async (
            tag: KidsCourseTag,
            isLoadMore = false,
            domain: Lang = Lang.Ru,
            ageGroups: KidsCourseAgeGroup[] = []
        ) => {
            const isProject = tag === KidsCourseTag.Projects;

            const currentPage = isProject
                ? projectsPageRef.current
                : materialsPageRef.current;
            const pageToFetch = isLoadMore ? currentPage + 1 : 0;

            if (isProject) setIsProjectsLoading(true);
            else setIsMaterialsLoading(true);

            try {
                const params: GetKidsCourses = {
                    domain,
                    tag,
                    page: pageToFetch,
                    size: PAGE_SIZE,
                };

                if (ageGroups.length > 0) {
                    params.ageGroups = ageGroups;
                }

                const response = await apiService.getList(params);
                const data = response.data;

                if (isProject) {
                    setProjects((prev) =>
                        isLoadMore ? [...prev, ...data.items] : data.items
                    );
                    setProjectsTotal(data.count);
                    projectsPageRef.current = pageToFetch;
                } else {
                    setMaterials((prev) =>
                        isLoadMore ? [...prev, ...data.items] : data.items
                    );
                    setMaterialsTotal(data.count);
                    materialsPageRef.current = pageToFetch;
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isProject) setIsProjectsLoading(false);
                else setIsMaterialsLoading(false);
            }
        },
        [apiService]
    );

    const hasMoreProjects = projects.length < projectsTotal;
    const hasMoreMaterials = materials.length < materialsTotal;

    return (
        <KidsCoursesContext.Provider
            value={{
                projects,
                materials,
                settings,
                isProjectsLoading,
                isMaterialsLoading,
                isSettingsLoading,
                hasMoreProjects,
                hasMoreMaterials,
                fetchItems,
                projectsTotal,
                materialsTotal,
                fetchSettings,
            }}>
            {children}
        </KidsCoursesContext.Provider>
    );
};

export const useKidsCourses = () => {
    const context = useContext(KidsCoursesContext);
    if (!context) {
        throw new Error(
            'useKidsCourses must be used within a KidsCoursesProvider'
        );
    }
    return context;
};
